import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  TextField as MuiTextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Chip,
  ButtonGroup,
  Button,
  IconButton,
  Collapse,
  Tooltip,
  TablePagination,
} from "@mui/material";
import moment from "moment";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";


/**
 * MUITable -  configurable table component with search, filtering, and expandable rows
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of data objects to display
 * @param {Array} props.columns - Column configuration array
 * @param {Array} props.filters - Filter configuration array
 * @param {Object} props.searchConfig - Search configuration
 * @param {Object} props.expandableConfig - Expandable rows configuration
 * @param {Object} props.actionsConfig - Actions configuration
 * @param {Object} props.loadingState - Loading state configuration
 * @param {Object} props.emptyState - Empty state configuration
 * @param {Object} props.paginationConfig - Pagination configuration
 * @param {Object} props.sortConfig - Sorting configuration
 * @param {Function} props.onRowClick - Callback for row clicks
 * @param {Function} props.onSearch - Callback for search changes
 * @param {Function} props.onFilterChange - Callback for filter changes
 * @param {Function} props.onAction - Callback for action clicks
 * @param {Object} props.styles - Custom styles object
 */
function MUITable({
  data = [],
  columns = [],
  filters = [],
  searchConfig = {},
  expandableConfig = {},
  actionsConfig = {},
  loadingState = {},
  emptyState = {},
  paginationConfig = {},
  sortConfig = {},
  onRowClick,
  onSearch,
  onFilterChange,
  onAction,
  styles = {},
}) {
  const useStyles = makeStyles((theme) => ({
    active: {
      fontWeight: "bold",
      backgroundColor: `${theme.palette.primary.main} !important`,
      color: `${theme.palette.primary.contrastText} !important`,
      borderColor: `${theme.palette.primary.main} !important`,
      '&:hover': {
        backgroundColor: `${theme.palette.primary.dark} !important`,
        borderColor: `${theme.palette.primary.dark} !important`,
      }
    },
    inactive: {
      fontWeight: "bold",
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'rgba(0, 51, 102, 0.04)',
      }
    },
    statusChip: {
      marginTop: '8px',
      '& .MuiChip-root': {
        backgroundColor: '#000',
        color: 'white',
        fontWeight: 500,
      },
    },
  }));

  const classes = useStyles();
  const [searchParams, setSearchParams] = useState(searchConfig.initialParams || {});
  const [focus, setFocus] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const isUserTypingRef = useRef(false);

  useEffect(() => {
    if (searchConfig.initialParams && !isUserTypingRef.current) {
      const currentSearch = searchParams.search || '';
      const newSearch = searchConfig.initialParams.search || '';
      const currentPage = searchParams.page || 1;
      const newPage = searchConfig.initialParams.page || 1;
      const currentLimit = searchParams.limit || 10;
      const newLimit = searchConfig.initialParams.limit || 10;
      
      if (currentSearch !== newSearch || currentPage !== newPage || currentLimit !== newLimit) {
        setSearchParams(searchConfig.initialParams);
      }
    }
    if (isUserTypingRef.current) {
      const timer = setTimeout(() => {
        isUserTypingRef.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [searchConfig.initialParams?.search, searchConfig.initialParams?.page, searchConfig.initialParams?.limit]);

  const {
    enabled: searchEnabled = true,
    placeholder = "Search...",
    fields = ['search']
  } = searchConfig;

  const {
    enabled: expandableEnabled = false,
    renderExpandedContent,
    expandTriggerField
  } = expandableConfig;

  const {
    enabled: actionsEnabled = false,
    actions = []
  } = actionsConfig;

  const {
    isLoading = false,
    loadingText = "Loading..."
  } = loadingState;

  const {
    message = "No data found",
    showWhenEmpty = true
  } = emptyState;

  const {
    enabled: paginationEnabled = false,
    total = 0,
    page = 0,
    rowsPerPage = 10,
    rowsPerPageOptions = [10, 25, 50, 100],
    onPageChange,
    onRowsPerPageChange,
    labelRowsPerPage = "Rows per page:",
    labelDisplayedRows,
  } = paginationConfig;

  const {
    enabled: sortEnabled = false,
    sortableFields = [],
    sortBy: externalSortBy = null,
    sortOrder: externalSortOrder = 'asc',
    onSortChange,
    serverSide = true,
  } = sortConfig;

  // Internal state for client-side sorting
  const [internalSortBy, setInternalSortBy] = useState(null);
  const [internalSortOrder, setInternalSortOrder] = useState('asc');

  // Use external sort state if server-side, otherwise use internal
  const sortBy = serverSide ? externalSortBy : internalSortBy;
  const sortOrder = serverSide ? externalSortOrder : internalSortOrder;

  const handleSearchChange = (field, value) => {
    isUserTypingRef.current = true;
    const newParams = {
      ...searchParams,
      [field]: value
    };
    setSearchParams(newParams);
  };
  
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    isUserTypingRef.current = false;
    if (onSearch) {
      onSearch(searchParams);
    }
  };

  const handleSearchIconClick = () => {
    isUserTypingRef.current = false;
    if (onSearch) {
      onSearch(searchParams);
    }
  };

  const setSearchFocus = () => {
    setFocus(true);
  };

  const unsetSearchFocus = () => {
    setFocus(false);
  };

  const CurrentLookupTypeButton = () => {
    return (
      <IconButton onClick={handleSearchIconClick} aria-label="Search">
        <SearchIcon />
      </IconButton>
    );
  };

  const handleFilterChange = (filterKey, value) => {
    const newParams = {
      ...searchParams,
      [filterKey]: value
    };
    setSearchParams(newParams);
    if (onFilterChange) {
      onFilterChange(filterKey, value, newParams);
    }
  };

  const handleRowClick = (rowData) => {
    if (onRowClick) {
      onRowClick(rowData);
    }
  };

  const handleActionClick = (actionKey, rowData) => {
    if (onAction) {
      onAction(actionKey, rowData);
    }
  };

  const handleExpandClick = (rowId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleSortRequest = (field) => {
    if (!sortEnabled || !sortableFields.includes(field)) {
      return;
    }

    let newSortBy = field;
    let newSortOrder = 'asc';

    if (sortBy === field) {
      // Toggle direction: asc -> desc -> asc
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }

    if (serverSide) {
      // Server-side sorting: call callback
      if (onSortChange) {
        onSortChange(newSortBy, newSortOrder);
      }
    } else {
      // Client-side sorting: update internal state
      setInternalSortBy(newSortBy);
      setInternalSortOrder(newSortOrder);
    }
  };

  // Client-side sorting logic
  const getSortedData = () => {
    if (!sortEnabled || serverSide || !sortBy) {
      return data;
    }

    const sortedData = [...data];
    sortedData.sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle dates
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortOrder === 'asc' 
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      // Handle strings
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (sortOrder === 'asc') {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });

    return sortedData;
  };

  const displayData = sortEnabled && !serverSide ? getSortedData() : data;

  const getFilterLabel = (filter, value) => {
    if (filter.getLabel) {
      return filter.getLabel(value);
    }
    return filter.options?.find(opt => opt.value === value)?.label || value || 'All';
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit(e);
    }
  };

  const SearchBar = () => {
    if (!searchEnabled) return null;

    return (
      <Box component="form" onSubmit={handleSearchSubmit} sx={{ width: '100%', mt: 2, mb: 2 }}>
        {focus ? (
          <MuiTextField
            id="searchTarget"
            type="text"
            placeholder={placeholder}
            onChange={(e) => handleSearchChange('search', e.target.value)}
            onKeyPress={handleKeyPress}
            autoFocus={true}
            onBlur={unsetSearchFocus}
            inputRef={text => text && text.focus()}
            name="searchTarget"
            value={searchParams.search || ''}
            fullWidth
            size="small"
            variant="outlined"
            slotProps={{ 
              inputLabel: { shrink: true },
              input: { endAdornment: <CurrentLookupTypeButton /> }
            }}
            sx={{ width: '100%', margin: 0 }}
          />
        ) : (
          <MuiTextField
            id="searchTarget"
            type="text"
            placeholder={placeholder}
            onChange={(e) => handleSearchChange('search', e.target.value)}
            onKeyPress={handleKeyPress}
            onClick={setSearchFocus}
            name="searchTarget"
            value={searchParams.search || ''}
            fullWidth
            size="small"
            variant="outlined"
            slotProps={{ 
              inputLabel: { shrink: true },
              input: { endAdornment: <CurrentLookupTypeButton /> }
            }}
            sx={{ width: '100%', margin: 0 }}
          />
        )}
      </Box>
    );
  };

  const FilterButtons = ({ filter }) => {
    const currentValue = searchParams[filter.key] || '';
    
    return (
      <Box className="p-3 mb-3">
        <ButtonGroup color="primary">
          <Button
            className={
              currentValue === ''
                ? classes.active
                : classes.inactive
            }
            variant={currentValue === '' ? 'contained' : 'outlined'}
            onClick={() => handleFilterChange(filter.key, '')}
          >
            All
          </Button>
          {filter.options?.map((option) => (
            <Button
              key={option.value}
              className={
                currentValue === option.value
                  ? classes.active
                  : classes.inactive
              }
              variant={currentValue === option.value ? 'contained' : 'outlined'}
              onClick={() => handleFilterChange(filter.key, option.value)}
            >
              {option.label}
            </Button>
          ))}
        </ButtonGroup>
        <div className={classes.statusChip}>
          <Chip label={getFilterLabel(filter, currentValue)} size="small"/>
        </div>
      </Box>
    );
  };

  const ExpandableRow = ({ rowData, isExpanded }) => {
    if (!expandableEnabled || !renderExpandedContent) return null;

    return (
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length + (actionsEnabled ? actions.length : 0)}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ marginTop: 1 }}>
              {renderExpandedContent(rowData)}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const renderCellContent = (column, rowData) => {
    const { field, type, render, ...columnProps } = column;
    
    if (render) {
      return render(rowData, columnProps);
    }

    const value = rowData[field];
    const rowId = rowData.id || rowData.uuid;
    const isExpanded = expandedRows.has(rowId);
    const isExpandableField = expandableEnabled && field === expandTriggerField;

    switch (type) {
      case 'chip':
        return (
          <Chip
            label={value}
            {...columnProps}
            size="small"
          />
        );
      case 'date':
        return value ? moment(value).format("L") : 'N/A';
      case 'status':
        return (
          <Chip
            label={value ? "Active" : "Inactive"}
            color={value ? "success" : "default"}
            size="small"
          />
        );
      case 'count':
        // Handle different count field types
        let countValue = value;
        if (field === 'properties') {
          const properties = rowData.properties || rowData.propertyList || rowData.property_list || rowData.items || [];
          countValue = Array.isArray(properties) ? properties.length : 0;
        }
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '5px' }}>{countValue || 0}</span>
            {isExpandableField && (
              <IconButton size="small">
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>
        );
      default:
        return value || 'N/A';
    }
  };

  const renderActions = (rowData) => {
    if (!actionsEnabled || !actions.length) return null;

    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        {actions.map((action, index) => {
          const { key, icon: Icon, tooltip, disabled, color = "primary" } = action;
          const isDisabled = typeof disabled === 'function' ? disabled(rowData) : disabled;
          const tooltipText = typeof tooltip === 'function' ? tooltip(rowData) : tooltip;

          return (
            <Tooltip key={index} title={tooltipText} placement="top">
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleActionClick(key, rowData);
                  }}
                  color={color}
                  disabled={isDisabled}
                >
                  <Icon />
                </IconButton>
              </span>
            </Tooltip>
          );
        })}
      </Box>
    );
  };

  return (
    <div style={{ width: '100%', margin: 0, padding: 0, ...styles.container }}>
      <SearchBar />

      <Grid container spacing={2}>
        {filters.map((filter, index) => (
          <Grid item xs={12} md={12} key={index}>
            <FilterButtons filter={filter} />
          </Grid>
        ))}
      </Grid>

      <Paper>
        <TableContainer>
          {isLoading ? (
            <Box p={3} textAlign="center">
              <CircularProgress />
              <Typography>{loadingText}</Typography>
            </Box>
          ) : data.length === 0 && showWhenEmpty ? (
            <Box p={3} textAlign="center">
              <Typography color="textSecondary">
                {message}
              </Typography>
            </Box>
          ) : (
            <Table aria-label="data table">
              <TableHead>
                <TableRow>
                  {columns.map((column, index) => {
                    const isSortable = sortEnabled && sortableFields.includes(column.field);
                    const isActive = sortBy === column.field;
                    
                    return (
                      <TableCell 
                        key={index}
                        sortDirection={isActive ? sortOrder : false}
                      >
                        {isSortable ? (
                          <TableSortLabel
                            active={isActive}
                            direction={isActive ? sortOrder : 'asc'}
                            onClick={() => handleSortRequest(column.field)}
                            sx={{
                              '& .MuiTableSortLabel-icon': {
                                opacity: isActive ? 1 : 0.5,
                                fontSize: '1.2rem',
                                color: isActive ? 'primary.main' : 'text.secondary',
                              },
                              '&:hover .MuiTableSortLabel-icon': {
                                opacity: 1,
                                color: 'primary.main',
                              },
                              '&.Mui-active': {
                                color: 'primary.main',
                                fontWeight: 600,
                              },
                              cursor: 'pointer',
                              userSelect: 'none',
                            }}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    );
                  })}
                  {actionsEnabled && actions.length > 0 && (
                    <TableCell>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayData.map((rowData) => {
                  const rowId = rowData.id || rowData.uuid;
                  const isExpanded = expandedRows.has(rowId);
                  
                  return (
                    <React.Fragment key={rowId}>
                      <TableRow 
                        hover
                        onClick={() => handleRowClick(rowData)}
                        style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                      >
                        {columns.map((column, index) => (
                          <TableCell key={index}>
                            {column.field === expandTriggerField && expandableEnabled ? (
                              <Box
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExpandClick(rowId);
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                {renderCellContent(column, rowData)}
                              </Box>
                            ) : (
                              renderCellContent(column, rowData)
                            )}
                          </TableCell>
                        ))}
                        {actionsEnabled && (
                          <TableCell>
                            {renderActions(rowData)}
                          </TableCell>
                        )}
                      </TableRow>
                      <ExpandableRow rowData={rowData} isExpanded={isExpanded} />
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        
        {paginationEnabled && !isLoading && (
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            SelectProps={{
              inputProps: {
                'aria-label': 'rows per page',
              },
              native: true,
            }}
            labelRowsPerPage={labelRowsPerPage}
            labelDisplayedRows={labelDisplayedRows}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',

              '& .MuiTablePagination-toolbar': {
                paddingLeft: 2,
                paddingRight: 2,
                display: 'flex',
                alignItems: 'center',
                visibility: 'visible !important',
              },

              '& .MuiTablePagination-actions': {
                display: 'flex !important',
                visibility: 'visible !important',
                marginLeft: 2,
                width: 'auto !important',
                minWidth: '160px !important',

                '& .MuiIconButton-root': {
                  width: '40px !important',
                  height: '40px !important',
                  display: 'inline-flex !important',
                  visibility: 'visible !important',
                  opacity: '1 !important',
                  color: 'text.secondary',
                  backgroundColor: 'background.paper',
                  '&:hover:not(.Mui-disabled)': {
                    backgroundColor: 'action.hover !important',
                    borderColor: 'text.secondary',
                    color: 'text.primary',
                  },
                  '&:active:not(.Mui-disabled)': {
                    backgroundColor: 'action.selected !important',
                    transform: 'translateY(1px)',
                  },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: 2,
                  },

                  '&.Mui-disabled': {
                    color: 'text.disabled',
                    opacity: 1,
                    cursor: 'not-allowed',
                  },
                },
              },

              '& .MuiTablePagination-selectLabel': { marginBottom: 0, visibility: 'visible !important' },
              '& .MuiTablePagination-displayedRows': { marginBottom: 0, visibility: 'visible !important' },

              '& .MuiTextField-root .MuiOutlinedInput-root:hover': {
                backgroundColor: 'action.selected !important',
              },
            }}
          />
        )}
      </Paper>
    </div>
  );
}

MUITable.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.string,
    render: PropTypes.func,
  })),
  filters: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })),
    getLabel: PropTypes.func,
  })),
  searchConfig: PropTypes.shape({
    enabled: PropTypes.bool,
    placeholder: PropTypes.string,
    fields: PropTypes.array,
    initialParams: PropTypes.object,
  }),
  expandableConfig: PropTypes.shape({
    enabled: PropTypes.bool,
    renderExpandedContent: PropTypes.func,
    expandTriggerField: PropTypes.string,
  }),
  actionsConfig: PropTypes.shape({
    enabled: PropTypes.bool,
    actions: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      tooltip: PropTypes.string,
      disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      color: PropTypes.string,
    })),
  }),
  loadingState: PropTypes.shape({
    isLoading: PropTypes.bool,
    loadingText: PropTypes.string,
  }),
  emptyState: PropTypes.shape({
    message: PropTypes.string,
    showWhenEmpty: PropTypes.bool,
  }),
  paginationConfig: PropTypes.shape({
    enabled: PropTypes.bool,
    total: PropTypes.number,
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
    onPageChange: PropTypes.func,
    onRowsPerPageChange: PropTypes.func,
    labelRowsPerPage: PropTypes.string,
    labelDisplayedRows: PropTypes.func,
  }),
  sortConfig: PropTypes.shape({
    enabled: PropTypes.bool,
    sortableFields: PropTypes.arrayOf(PropTypes.string),
    sortBy: PropTypes.string,
    sortOrder: PropTypes.oneOf(['asc', 'desc']),
    onSortChange: PropTypes.func,
    serverSide: PropTypes.bool,
  }),
  onRowClick: PropTypes.func,
  onSearch: PropTypes.func,
  onFilterChange: PropTypes.func,
  onAction: PropTypes.func,
  styles: PropTypes.object,
};

export default MUITable;
